// Shared upload runner for the CSV bulk-resolve/bulk-comment modals: parse the sheet, then send
// the rows SLICE_SIZE at a time to one bulk endpoint, with live progress + cancel. One request
// per row (the earlier approach) tripped nginx's per-IP request limit on large sheets — 429s,
// which the browser shows as CORS errors since nginx's 429 carries no CORS headers.
import * as XLSX from 'xlsx/xlsx.mjs';

export const SLICE_SIZE = 100;
const SLICE_GAP_MS = 200;

export const parseSheet1 = (binary) => {
    const workbook = XLSX.read(binary, { type: 'binary', cellDates: true });
    let rows = [];
    workbook.SheetNames.forEach((sheetName) => {
        const roa = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName], { header: 0, raw: false, dateNF: 'yyyy-mm-dd' });
        if (sheetName === 'Sheet1' && roa.length > 0) rows = roa;
    });
    return rows.filter((row) => !isBlankRow(row));
};

// A row whose every cell is empty isn't a ticket — e.g. the sample's second row after its
// ticket number is cleared leaves an empty "reason" cell behind, which the sheet reader still
// returns as a row. Without this it was processed and reported "Missing ticket_number".
const isBlankRow = (row) => Object.values(row).every((v) => v === null || v === undefined || String(v).trim() === '');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SERVER_STATUS = { SUCCESS: 'Success', SKIPPED: 'Skipped', FAILED: 'Failed' };

/**
 * Sends the sheet SLICE_SIZE rows per request, with cooperative cancel + live progress.
 *   validate(row) -> { error } for a row that can't be sent (reported Failed, never sent), or
 *                    { item } — the row as the bulk endpoint expects it.
 *   send(items)   -> the endpoint's BulkActionResult (results in the same order as items).
 *   describe(serverItem, row) -> optional message override for a row's result.
 * Results are { ticketNumber, status: Success|Skipped|Failed|Cancelled, message }, in sheet order.
 */
export const runBulkInSlices = async (rows, { validate, send, describe, rowLabel, sliceSize = SLICE_SIZE }, setResults, cancelRef) => {
    const labelOf = (row, index) => (rowLabel ? rowLabel(row, index) : row.ticket_number?.toString().trim() || '');
    const results = [];
    for (let i = 0; i < rows.length; i += sliceSize) {
        if (cancelRef.current) {
            rows.slice(i).forEach((row, j) => results.push({ ticketNumber: labelOf(row, i + j), status: 'Cancelled', message: 'Stopped by user' }));
            break;
        }
        const slice = rows.slice(i, i + sliceSize);
        const prepared = slice.map((row) => ({ row, ...validate(row) }));
        const toSend = prepared.filter((p) => p.item);
        let serverResults = [];
        let sliceError = null;
        if (toSend.length) {
            try {
                const response = await send(toSend.map((p) => p.item));
                serverResults = response?.data?.results || [];
            } catch (err) {
                sliceError = err?.response?.data?.message || err?.message || 'Request failed';
            }
        }
        let next = 0;
        prepared.forEach((p, j) => {
            const ticketNumber = labelOf(p.row, i + j);
            if (!p.item) {
                results.push({ ticketNumber, status: 'Failed', message: p.error });
                return;
            }
            const server = serverResults[next++];
            if (sliceError || !server) {
                results.push({ ticketNumber, status: 'Failed', message: sliceError || 'No result returned' });
                return;
            }
            const status = SERVER_STATUS[server.status] || 'Failed';
            results.push({ ticketNumber, status, message: describe?.(server, p.row) || server.message });
        });
        setResults([...results]);
        if (i + sliceSize < rows.length && !cancelRef.current) await sleep(SLICE_GAP_MS);
    }
    return results;
};

export const readFileAsRows = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        try {
            resolve(parseSheet1(e.target.result));
        } catch (err) {
            reject(err);
        }
    };
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsBinaryString(file);
});

// Generates a sample .xlsx entirely client-side (no backend round trip needed) — rows is an
// array of plain objects, keys become the header row, sheet is named "Sheet1" to match what
// parseSheet1 expects back on upload.
export const downloadSampleSheet = (rows, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, fileName);
};

// The same sample, with a dropdown on some columns (dropdowns: { column: [values] }). The values
// sit on a hidden "Lists" sheet, so a value with a comma still works; upload reads only Sheet1.
// exceljs is imported on demand so it stays out of the main bundle.
export const downloadSampleSheetWithDropdowns = async (rows, fileName, dropdowns = {}) => {
    const mod = await import('exceljs');
    const ExcelJS = mod.default || mod;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');
    const columns = Object.keys(rows[0] || {});
    sheet.columns = columns.map((key) => ({ header: key, key, width: Math.max(14, key.length + 4) }));
    rows.forEach((row) => sheet.addRow(row));

    const lists = workbook.addWorksheet('Lists', { state: 'hidden' });
    Object.entries(dropdowns).forEach(([column, values], i) => {
        const at = columns.indexOf(column);
        if (at < 0 || !values.length) return;
        const listCol = lists.getColumn(i + 1).letter;
        values.forEach((v, r) => { lists.getCell(`${listCol}${r + 1}`).value = v; });
        const formulae = [`Lists!$${listCol}$1:$${listCol}$${values.length}`];
        // One range, not one rule per cell: exceljs merges per-cell rules into overlapping ranges.
        const letter = sheet.getColumn(at + 1).letter;
        sheet.dataValidations.add(`${letter}2:${letter}1000`, {
            type: 'list', allowBlank: true, formulae, showErrorMessage: true,
            errorTitle: 'Unknown value', error: `Pick a ${column} from the list, or leave it blank.`,
        });
    });

    const buf = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
